import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/app/PageHeader";
import { inr, fmtDate, computeCommission } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, PencilSimple, MagnifyingGlass, CheckCircle, ClockCountdown, Trash, FunnelSimple } from "@phosphor-icons/react";

const LEAD_SOURCES = ["Paisapaid Inhouse Team", "Paisapaid Agents Team", "Own Brand Partners Team"];
const DELIVERY = ["CRM", "WhatsApp"];
const STATUSES = ["Pending", "Paid"];

const blank = {
  disbursement_date: new Date().toISOString().slice(0, 10),
  lead_source: "", team_type: "", team_id: "", team_name: "",
  product_id: "", lead_delivery_type: "", customer_name: "", customer_mobile: "",
  customer_email: "", state: "", employee_id: "", loan_amount: 0, lender_id: "",
  commission_type: "percentage", commission_percent: 0, gst_percent: 0, tds_percent: 0,
  agent_share_percent: 0, agent_has_gst: false, agent_commission_amount: 0, other_deductions: 0, invoice_generate: false, status: "Pending",
};

export default function Disbursements() {
  const { can, isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [payDlg, setPayDlg] = useState({ open: false, row: null });
  const [payForm, setPayForm] = useState({ payment_date: "", payment_mode: "", reference: "", remarks: "" });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [teamOptions, setTeamOptions] = useState([]);
  const [lenders, setLenders] = useState([]);
  const [products, setProducts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [delId, setDelId] = useState(null);
  const [flt, setFlt] = useState({ source: "", employee: "", frm: "", to: "", min: "", max: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.q = search;
      if (statusFilter) params.status = statusFilter;
      const res = await api.get("/disbursements", { params });
      setRows(res.data);
    } catch { toast.error("Failed to load disbursements"); }
    finally { setLoading(false); }
  }, [search, statusFilter]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  useEffect(() => {
    api.get("/lenders").then((r) => setLenders(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/loan-backend-employees").then((r) => setEmployees(r.data)).catch(() => {});
  }, []);

  const fetchTeams = useCallback(async (source) => {
    if (!source) { setTeamOptions([]); return; }
    try { const r = await api.get("/team-options", { params: { source } }); setTeamOptions(r.data.options.map((o) => ({ ...o, type: r.data.type }))); }
    catch { setTeamOptions([]); }
  }, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onLeadSource = (v) => { setForm((p) => ({ ...p, lead_source: v, team_id: "", team_name: "", team_type: "" })); fetchTeams(v); };
  const onTeam = (id) => {
    const t = teamOptions.find((o) => o.id === id);
    set("team_id", id); set("team_name", t?.label || ""); set("team_type", t?.type || "");
  };
  const onLender = (id) => {
    const l = lenders.find((x) => x.id === id);
    setForm((p) => ({ ...p, lender_id: id,
      commission_percent: l?.default_commission_percent ?? p.commission_percent,
      gst_percent: l?.default_gst_percent ?? p.gst_percent,
      tds_percent: l?.default_tds_percent ?? p.tds_percent }));
  };

  const calc = computeCommission(form);

  const openCreate = () => { setForm(blank); setEditing(null); setTeamOptions([]); setOpen(true); };
  const openEdit = (row) => {
    const gross = row?.calc?.gross_commission || 0;
    const pct = row.agent_share_percent || (gross > 0 && row?.calc?.agent_commission ? (row.calc.agent_commission / gross) * 100 : 0);
    setForm({ ...blank, ...row, agent_share_percent: Math.round(pct * 100) / 100, agent_commission_amount: 0 });
    setEditing(row); setOpen(true);
    if (row.lead_source) fetchTeams(row.lead_source);
  };

  const submit = async () => {
    if (!form.customer_name) { toast.error("Customer Name is required"); return; }
    const payload = {
      ...form,
      loan_amount: Number(form.loan_amount || 0),
      commission_percent: Number(form.commission_percent || 0),
      gst_percent: Number(form.gst_percent || 0),
      tds_percent: Number(form.tds_percent || 0),
      agent_share_percent: Number(form.agent_share_percent || 0),
      agent_has_gst: !!form.agent_has_gst,
      agent_commission_amount: 0,
      agent_id: form.team_type === "agent" ? form.team_id : null,
      partner_id: form.team_type === "partner" ? form.team_id : null,
    };
    setSaving(true);
    try {
      if (editing) { await api.put(`/disbursements/${editing.id}`, payload); toast.success("Disbursement updated"); }
      else { await api.post("/disbursements", payload); toast.success("Disbursement created"); }
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const changeStatus = async (row, status) => {
    if (status === "Paid") {
      setPayForm({ payment_date: new Date().toISOString().slice(0, 10), payment_mode: "", reference: "", remarks: "" });
      setPayDlg({ open: true, row });
      return;
    }
    try { await api.post(`/disbursements/${row.id}/status`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const confirmPaid = async () => {
    if (!payForm.payment_date || !payForm.payment_mode) { toast.error("Please enter payment date and mode"); return; }
    try {
      await api.post(`/disbursements/${payDlg.row.id}/status`, { status: "Paid", payment_details: payForm });
      toast.success("Marked Paid with payment details");
      setPayDlg({ open: false, row: null }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to mark paid"); }
  };

  const lenderName = (id) => lenders.find((l) => l.id === id)?.name || "—";
  const productName = (id) => products.find((p) => p.id === id)?.product_name || "—";

  const doDelete = async () => { try { await api.delete(`/disbursements/${delId}`); toast.success("Disbursement removed"); setOpen(false); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); } finally { setDelId(null); } };

  const displayRows = rows.filter((r) => {
    if (flt.source && r.lead_source !== flt.source) return false;
    if (flt.employee && r.employee_id !== flt.employee) return false;
    const d = (r.disbursement_date || "").slice(0, 10);
    if (flt.frm && d < flt.frm) return false;
    if (flt.to && d > flt.to) return false;
    const amt = Number(r.loan_amount || 0);
    if (flt.min !== "" && amt < Number(flt.min)) return false;
    if (flt.max !== "" && amt > Number(flt.max)) return false;
    return true;
  });
  const filteredLoanTotal = displayRows.reduce((s, r) => s + Number(r.loan_amount || 0), 0);
  const hasFilters = flt.source || flt.employee || flt.frm || flt.to || flt.min !== "" || flt.max !== "";

  return (
    <div data-testid="disbursements-page">
      <PageHeader overline="Loan Backend" title="Disbursement" subtitle="Record loan disbursements and commission."
        actions={can("loan.add") && (
          <Button data-testid="disbursement-add-button" onClick={openCreate} className="rounded-sm bg-brand hover:bg-brand-dark">
            <Plus size={16} className="mr-1.5" weight="bold" /> New Disbursement
          </Button>
        )} />

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative w-72 max-w-full">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input data-testid="disbursement-search" placeholder="Search customer, mobile…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 rounded-sm border-zinc-200" />
        </div>
        <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="disbursement-status-filter" className="w-44 rounded-sm border-zinc-200"><SelectValue placeholder="All Payment Status" /></SelectTrigger>
          <SelectContent className="rounded-sm">
            <SelectItem value="all">All Status</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-zinc-400 mb-1 flex items-center gap-1"><FunnelSimple size={12} /> Team</Label>
          <Select value={flt.source || "all"} onValueChange={(v) => setFlt({ ...flt, source: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="disb-filter-team" className="w-56 rounded-sm border-zinc-200 h-9"><SelectValue placeholder="All Teams" /></SelectTrigger>
            <SelectContent className="rounded-sm"><SelectItem value="all">All Teams</SelectItem>{LEAD_SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-zinc-400 mb-1 block">Person</Label>
          <Select value={flt.employee || "all"} onValueChange={(v) => setFlt({ ...flt, employee: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="disb-filter-person" className="w-48 rounded-sm border-zinc-200 h-9"><SelectValue placeholder="All People" /></SelectTrigger>
            <SelectContent className="rounded-sm max-h-64"><SelectItem value="all">All People</SelectItem>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-[11px] uppercase tracking-wider text-zinc-400 mb-1 block">From</Label><Input data-testid="disb-filter-from" type="date" value={flt.frm} onChange={(e) => setFlt({ ...flt, frm: e.target.value })} className="h-9 rounded-sm w-40" /></div>
        <div><Label className="text-[11px] uppercase tracking-wider text-zinc-400 mb-1 block">To</Label><Input data-testid="disb-filter-to" type="date" value={flt.to} onChange={(e) => setFlt({ ...flt, to: e.target.value })} className="h-9 rounded-sm w-40" /></div>
        <div><Label className="text-[11px] uppercase tracking-wider text-zinc-400 mb-1 block">Min ₹</Label><Input data-testid="disb-filter-min" type="number" value={flt.min} onChange={(e) => setFlt({ ...flt, min: e.target.value })} className="h-9 rounded-sm w-28" placeholder="0" /></div>
        <div><Label className="text-[11px] uppercase tracking-wider text-zinc-400 mb-1 block">Max ₹</Label><Input data-testid="disb-filter-max" type="number" value={flt.max} onChange={(e) => setFlt({ ...flt, max: e.target.value })} className="h-9 rounded-sm w-28" placeholder="∞" /></div>
        {hasFilters && <Button variant="ghost" size="sm" className="rounded-sm h-9 text-zinc-500" data-testid="disb-filter-clear" onClick={() => setFlt({ source: "", employee: "", frm: "", to: "", min: "", max: "" })}>Clear</Button>}
        <div className="ml-auto text-sm text-zinc-500" data-testid="disb-filter-total">{displayRows.length} deals · <span className="font-medium text-zinc-900 tabular-nums">{inr(filteredLoanTotal)}</span></div>
      </div>

      <div className="border border-zinc-200 bg-white rounded-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50 hover:bg-zinc-50">
              {(isAdmin()
                ? ["Date", "Customer", "Team", "Lender", "Product", "Loan Amt", "Gross", "Lender GST", "Agent", "Agent Net", "Net Company", "Payment Status", "Actions"]
                : ["Date", "Customer", "Team", "Lender", "Product", "Loan Amt", "Agent", "Agent Net", "Payment Status", "Actions"]
              ).map((h) => (
                <TableHead key={h} className={`text-[11px] uppercase tracking-wider text-zinc-500 font-medium ${h === "Actions" ? "text-right" : ""}`}>{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={isAdmin() ? 13 : 10} className="text-center py-12 text-zinc-400">Loading…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={isAdmin() ? 13 : 10} className="text-center py-12 text-zinc-400">No disbursements yet.</TableCell></TableRow>
            ) : displayRows.length === 0 ? (
              <TableRow><TableCell colSpan={isAdmin() ? 13 : 10} className="text-center py-12 text-zinc-400">No disbursements match the filters.</TableCell></TableRow>
            ) : displayRows.map((r) => (
              <TableRow key={r.id} data-testid="disbursement-row" className="border-zinc-100 hover:bg-zinc-50">
                <TableCell className="text-sm text-zinc-600 whitespace-nowrap">{fmtDate(r.disbursement_date)}</TableCell>
                <TableCell className="text-sm"><span className="font-medium text-zinc-900">{r.customer_name}</span><div className="text-xs text-zinc-400">{r.customer_mobile}</div></TableCell>
                <TableCell className="text-sm text-zinc-600">{r.team_name || "—"}<div className="text-[11px] text-zinc-400">{r.lead_source}</div></TableCell>
                <TableCell className="text-sm text-zinc-600">{lenderName(r.lender_id)}</TableCell>
                <TableCell className="text-sm text-zinc-600">{productName(r.product_id)}</TableCell>
                <TableCell className="text-sm tabular-nums">{inr(r.loan_amount)}</TableCell>
                {isAdmin() && <TableCell className="text-sm tabular-nums">{inr(r?.calc?.gross_commission)}</TableCell>}
                {isAdmin() && <TableCell className="text-sm tabular-nums text-zinc-500">{inr(r?.calc?.gst_amount)}</TableCell>}
                <TableCell className="text-sm tabular-nums text-zinc-500">{inr(r?.calc?.agent_commission)}</TableCell>
                <TableCell className="text-sm tabular-nums font-medium text-emerald-700">{inr(r?.calc?.agent_net_commission)}</TableCell>
                {isAdmin() && <TableCell className="text-sm tabular-nums font-medium text-brand">{inr(r?.calc?.net_company_commission)}</TableCell>}
                <TableCell>
                  <Badge variant="outline" className={`rounded-sm font-normal ${r.status === "Paid" ? "border-emerald-500 text-emerald-600" : "border-amber-500 text-amber-600"}`}>{r.status || "Pending"}</Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {can("loan.edit") && <Button data-testid="disbursement-edit-button" variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-zinc-500 hover:text-zinc-900" onClick={() => openEdit(r)}><PencilSimple size={16} /></Button>}
                    {can("loan.edit") && r.status !== "Paid" && <Button data-testid="disbursement-paid-button" variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-zinc-500 hover:text-emerald-600" title="Mark Paid" onClick={() => changeStatus(r, "Paid")}><CheckCircle size={16} /></Button>}
                    {can("loan.edit") && r.status === "Paid" && <Button data-testid="disbursement-pending-button" variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-zinc-500 hover:text-amber-600" title="Mark Pending" onClick={() => changeStatus(r, "Pending")}><ClockCountdown size={16} /></Button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl rounded-sm max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading tracking-tight">{editing ? "Edit Disbursement" : "New Disbursement"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <Field label="Disbursement Date"><Input data-testid="f-date" type="date" value={form.disbursement_date || ""} onChange={(e) => set("disbursement_date", e.target.value)} className="rounded-sm h-10" /></Field>
            <SelectField label="Lead Source" testid="f-lead-source" value={form.lead_source} onChange={onLeadSource} options={LEAD_SOURCES.map((v) => ({ value: v, label: v }))} />
            <SelectField label="Select Team" testid="f-team" value={form.team_id} onChange={onTeam} options={teamOptions.map((o) => ({ value: o.id, label: `${o.label}${o.code ? ` (${o.code})` : ""}` }))} placeholder={form.lead_source ? "Select team…" : "Choose lead source first"} />
            <SelectField label="Loan Product" testid="f-product" value={form.product_id} onChange={(v) => set("product_id", v)} options={products.map((p) => ({ value: p.id, label: p.product_name }))} />
            <SelectField label="Lead Delivery Type" testid="f-delivery" value={form.lead_delivery_type} onChange={(v) => set("lead_delivery_type", v)} options={DELIVERY.map((v) => ({ value: v, label: v }))} />
            <Field label="Customer Name *"><Input data-testid="f-customer" value={form.customer_name || ""} onChange={(e) => set("customer_name", e.target.value)} className="rounded-sm h-10" /></Field>
            <Field label="Mobile Number"><Input data-testid="f-mobile" value={form.customer_mobile || ""} onChange={(e) => set("customer_mobile", e.target.value)} className="rounded-sm h-10" /></Field>
            <Field label="Email"><Input data-testid="f-email" type="email" value={form.customer_email || ""} onChange={(e) => set("customer_email", e.target.value)} className="rounded-sm h-10" /></Field>
            <Field label="State"><Input data-testid="f-state" value={form.state || ""} onChange={(e) => set("state", e.target.value)} className="rounded-sm h-10" /></Field>
            <SelectField label="Loan Backend Employee" testid="f-employee" value={form.employee_id} onChange={(v) => set("employee_id", v)} options={employees.map((e) => ({ value: e.id, label: e.label }))} />
            <Field label="Loan Amount"><Input data-testid="f-amount" type="number" value={form.loan_amount} onChange={(e) => set("loan_amount", e.target.value)} className="rounded-sm h-10" /></Field>
            <SelectField label="Lender Name" testid="f-lender" value={form.lender_id} onChange={onLender} options={lenders.map((l) => ({ value: l.id, label: l.name }))} />
            <Field label="Commission %"><Input data-testid="f-commission" type="number" value={form.commission_percent} onChange={(e) => set("commission_percent", e.target.value)} className="rounded-sm h-10" /></Field>
            <Field label="GST %"><Input data-testid="f-gst" type="number" value={form.gst_percent} onChange={(e) => set("gst_percent", e.target.value)} className="rounded-sm h-10" /></Field>
            <Field label="TDS %"><Input data-testid="f-tds" type="number" value={form.tds_percent} onChange={(e) => set("tds_percent", e.target.value)} className="rounded-sm h-10" /></Field>
            <Field label="Agent Commission %"><Input data-testid="f-agent-pct" type="number" value={form.agent_share_percent} onChange={(e) => set("agent_share_percent", e.target.value)} className="rounded-sm h-10" placeholder="% of loan amount" /></Field>
            <Field label="Agent has GST certificate?">
              <div className="flex items-center gap-2 h-10">
                <Switch data-testid="f-agent-gst" checked={!!form.agent_has_gst} onCheckedChange={(v) => set("agent_has_gst", v)} />
                <span className="text-sm text-zinc-600">{form.agent_has_gst ? "Yes — GST added to agent commission" : "No — only TDS deducted"}</span>
              </div>
            </Field>
            <SelectField label="Payment Status" testid="f-status" value={form.status} onChange={(v) => set("status", v)} options={STATUSES.map((v) => ({ value: v, label: v }))} />
            <div className="flex items-center justify-between border border-zinc-200 rounded-sm px-3 h-10 self-end">
              <Label className="text-sm text-zinc-600">Invoice Generate</Label>
              <Switch data-testid="f-invoice" checked={!!form.invoice_generate} onCheckedChange={(v) => set("invoice_generate", v)} />
            </div>
          </div>

          <div className={`grid grid-cols-2 ${isAdmin() ? "sm:grid-cols-3" : "sm:grid-cols-4"} gap-px bg-zinc-200 border border-zinc-200 rounded-sm mt-2`}>
            {isAdmin() && <Calc label="Gross Commission" v={calc.gross_commission} />}
            {isAdmin() && <Calc label="GST from Lender" v={calc.gst_amount} testid="calc-lender-gst" />}
            <Calc label="Agent Commission" v={calc.agent_commission} />
            <Calc label="TDS (on Agent)" v={calc.agent_tds_amount} />
            <Calc label="GST (added)" v={calc.agent_gst_amount} />
            <Calc label="Agent Net Commission" v={calc.agent_net_commission} accent="agent" testid="calc-agent-net" />
            {isAdmin() && <Calc label="Net Company Profit" v={calc.net_company_commission} accent="company" testid="calc-net" />}
          </div>

          <DialogFooter className="mt-2">
            {editing && can("loan.delete") && <Button data-testid="disbursement-delete-button" variant="outline" className="rounded-sm mr-auto text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700" onClick={() => setDelId(editing.id)}><Trash size={16} className="mr-1.5" /> Delete</Button>}
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="disbursement-submit-button" onClick={submit} disabled={saving} className="rounded-sm bg-brand hover:bg-brand-dark">{saving ? "Saving…" : editing ? "Save changes" : "Create Disbursement"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={payDlg.open} onOpenChange={(v) => setPayDlg({ open: v, row: v ? payDlg.row : null })}>
        <DialogContent className="max-w-md rounded-sm" data-testid="payment-remarks-dialog">
          <DialogHeader><DialogTitle className="font-heading tracking-tight">Payment Details</DialogTitle></DialogHeader>
          <div className="space-y-4 py-1">
            <p className="text-sm text-zinc-500">{payDlg.row?.customer_name} · {inr(payDlg.row?.calc?.net_company_commission)} net</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Payment Date"><Input type="date" data-testid="pay-date" value={payForm.payment_date} onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })} className="rounded-sm h-10" /></Field>
              <div>
                <Label className="text-xs text-zinc-500 mb-1.5 block">Payment Mode</Label>
                <Select value={payForm.payment_mode || ""} onValueChange={(v) => setPayForm({ ...payForm, payment_mode: v })}>
                  <SelectTrigger data-testid="pay-mode" className="rounded-sm h-10"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent className="rounded-sm">{["Bank Transfer / NEFT", "UPI", "Cheque", "Cash", "Other"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <Field label="Reference / Transaction ID"><Input data-testid="pay-reference" value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} className="rounded-sm h-10" placeholder="UTR / Cheque no. / txn id" /></Field>
            <div>
              <Label className="text-xs text-zinc-500 mb-1.5 block">Remarks</Label>
              <textarea data-testid="pay-remarks" value={payForm.remarks} onChange={(e) => setPayForm({ ...payForm, remarks: e.target.value })} rows={3} className="w-full rounded-sm border border-zinc-200 p-2 text-sm" placeholder="How and when the payment was made…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setPayDlg({ open: false, row: null })}>Cancel</Button>
            <Button data-testid="payment-confirm-button" onClick={confirmPaid} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white">Confirm Paid</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader><AlertDialogTitle>Remove this disbursement?</AlertDialogTitle><AlertDialogDescription>This archives the record (soft delete) and removes it from reports.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel><AlertDialogAction data-testid="confirm-disbursement-delete" onClick={doDelete} className="rounded-sm bg-red-600 hover:bg-red-700">Remove</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><Label className="text-xs text-zinc-500 mb-1.5 block">{label}</Label>{children}</div>
);
const SelectField = ({ label, testid, value, onChange, options, placeholder = "Select…" }) => (
  <div>
    <Label className="text-xs text-zinc-500 mb-1.5 block">{label}</Label>
    <Select value={value ? String(value) : ""} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="rounded-sm h-10"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="rounded-sm max-h-64">
        {options.filter((o) => o.value !== "" && o.value != null).map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);
const Calc = ({ label, v, accent, testid }) => {
  const color = accent === "agent" ? "text-emerald-700" : accent === "company" ? "text-brand" : "text-zinc-900";
  return (
    <div data-testid={testid} className="bg-white p-3">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      <div className={`font-heading text-lg tabular-nums ${color}`}>{inr(v)}</div>
    </div>
  );
};
